<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span>{{ tractGroup?.name }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh @close="$emit('close')"/>
    </v-card-title>
    <v-card-text>
      <v-tabs
      class="mt-2"
      v-model="currentTab">
        <v-tab>
          {{ $t('properties') }}
        </v-tab>
        <v-tab>
          {{ $t('tracts') }}
        </v-tab>
        <v-tab>
          {{ $t('analysis') }}
        </v-tab>
      </v-tabs>
      <v-window v-model="currentTab" style="min-height: 600px;">
        <v-window-item class="mt-n5">
          <v-container fluid>
            <v-row class="mt-3">
              <v-col cols="12">
                <DetailCard :title="$t('generalInformation')">
                  <template #title.append>
                    <v-row dense>
                      <v-col>
                        <Icon
                          iconColor="info"
                          v-if="tractGroup?.requiresExt1"
                          icon="mdi-pound"
                          margin=""
                          :tooltipText="$t('requiresExt1')"
                        />
                      </v-col>
                      <v-col>
                        <Icon
                          iconColor="info"
                          v-if="tractGroup?.requiresExt2"
                          icon="mdi-pound"
                          margin=""
                          :tooltipText="$t('requiresExt2')"
                        />
                      </v-col>
                    </v-row>
                  </template>
                  <LoadableComponent :loading="loading" :height="250">
                    <v-row no-gutters v-for="row in generalInformationRows" :key="row.key">
                      <v-col cols="6" sm="5">{{ $t(row.key) }}:</v-col>
                      <v-col cols="6" sm="7">
                        <span :style="{ color: row.value === $t('notSpecified') ? 'rgba(0,0,0,0.4)' : undefined }">
                          {{ row.value }}
                        </span>
                      </v-col>
                    </v-row>
                  </LoadableComponent>
                </DetailCard>
              </v-col>
              <v-col cols="6" v-if="obligationRate">
                <DetailCard :title="$t('obligationRate')" :fillHeight="false">
                  <LoadableComponent :loading="loading" :height="150">
                    <v-row dense no-gutters class="mt-n2">
                      <v-col cols="6" sm="5">{{ $t('name') }}:</v-col>
                      <v-col cols="6" sm="7">{{ obligationRate.name }}</v-col>
                      <v-col cols="6" sm="5">{{ $t('payBy') }}:</v-col>
                      <v-col cols="6" sm="7">
                        <span class="mr-2">{{ PayBy.fromInt(obligationRate.costCalculation?.payBy) }}</span>
                        <Icon
                          v-if="obligationRate.costCalculation?.payBy === PayBy.Volume.value && obligationRate.costCalculation?.rateSheet"
                          icon="mdi-note-text"
                          :tooltipText="obligationRate.costCalculation.rateSheet"
                          @icon-clicked="showRateSheetDialog"/>
                      </v-col>
                      <v-col cols="6" sm="5">{{ $t('payOn') }}:</v-col>
                      <v-col cols="6" sm="7">{{ PayOn.fromInt(obligationRate.costCalculation?.payOn) }}</v-col>
                    </v-row>
                  </LoadableComponent>
                </DetailCard>
              </v-col>
              <v-col cols="12" :md="optionalCardWidths" v-if="tractGroup?.landowners?.length > 0">
                <LoadableComponent :loading="loading">
                  <v-expansion-panels popout tile v-model="openLandownersPanel">
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        <v-row dense class="subtitle-1 mt-n1" style="flex-wrap: nowrap; overflow: hidden; text-wrap: nowrap;">
                          <v-col>
                            <span> {{$t('landowners')}} </span>
                          </v-col>
                          <v-col cols="auto">
                            <span> {{ landownersPanelString }}
                            </span>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-data-table
                        class="mt-2 small-headers"
                        hide-default-footer
                        :items-per-page="-1"
                        :items="landowners"
                        :headers="landownerHeaders"
                        sort-by="ownership"
                        sort-desc
                        dense>
                        <template #item.name="{item}">
                          <v-row no-gutters align="center">
                            {{item.accountName}}
                          </v-row>
                        </template>
                        <template #item.ownership="{item}">
                          <span>{{formatPercentage(item.ownership)}}</span>
                        </template>
                        <template #item.isDefault="{item}">
                          <v-icon color="black" v-if="item.isDefault">
                            mdi-checkbox-marked-circle
                          </v-icon>
                        </template>
                      </v-data-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                </LoadableComponent>
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>
        <v-window-item>
          <v-container fluid>
            <v-row v-if="tractGroup?.tracts && tractGroup?.tracts.length > 0">
              <v-col cols="12">
                <LoadableComponent :loading="loading">
                  <DataTable
                  :headers="tractHeaders"
                  :items.sync="tractGroup.tracts"
                  :loading="loading"
                  :customCells="[{ slotName: 'status', value: 'status' }, { slotName: 'actions', value: 'actions' }]"
                  :showSearch="false"
                  @click:row="onTractClick"
                  showPointerCursor>
                    <template #status="{ item }">
                      <TractStatusIcon :status="item.status" :isHeader="false"/>
                    </template>
                    <template #actions="slotProps">
                      <td @click.stop.prevent class="text-center cursor-default">
                        <Icon
                        icon="mdi-pencil"
                        iconColor="success"
                        elementId="edit-tract-in-group"
                        :tooltipText="$t('edit')"
                        @icon-clicked="openEditTract(slotProps.item)"
                        :disabled="!userAssignedClaim(UserClaims.ContractManager)"/>
                      </td>
                    </template>
                  </DataTable>
                </LoadableComponent>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <LoadableComponent :loading="loading">
                  <NoData>
                    {{ $t('noTractsInGroup') }}
                  </NoData>
                </LoadableComponent>
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>
        <v-window-item>
          <TractGroupAnalysis
          v-if="tractGroup && currentTab === 2"
          :tractGroup="tractGroup"/>
        </v-window-item>
      </v-window>
    </v-card-text>
    <Dialog :stateId="rateSheetDialogId" @close="closeRateSheetDialog">
      <RateSheetViewer
      v-if="viewingRateSheetDetails"
      :accountId="purchasingAccountId"
      :rateSheet="viewingRateSheetDetails"/>
    </Dialog>
    <Dialog :stateId="tractDetailDialogId" @close="closeTractDetailDialog">
      <TractForm
      v-if="editingTractId"
      :tractId="editingTractId"
      @close="closeTractDetailDialog"/>
      <TractDetail
      v-else-if="focusedTractId"
      :tractId="focusedTractId"
      @edit-tract="editTract"
      @view-tract="focusedTractId = $event.tractId"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { TractTypeCategory, TractStatus, NamedRateSpecialization, PayBy, PayOn, UserClaims } from '@/utils/Enumerations'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { formatMoney, formatPercentage } from '@/utils/NumericMutations'
import { utcToLocalDate } from '@/utils/DateFormatter'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'TractGroupDetailDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    TractStatusIcon: () => import('@/components/tract/TractStatusIcon.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    RateSheetViewer: () => import('@/components/activity/rate-sheet/RateSheetViewer.vue'),
    TractDetail: () => import('@/components/tract/tract-detail/TractDetail.vue'),
    TractForm: () => import('@/components/tract/tract-form/TractForm.vue'),
    TractGroupAnalysis: () => import('@/components/tract/TractGroupAnalysis.vue')
  },

  props: {
    tractGroupId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    PayBy,
    PayOn,
    UserClaims,
    tractGroup: null,
    loading: true,
    TractStatus,
    openLandownersPanel: null,
    currentTab: 0,
    viewingRateSheetDetails: undefined,
    rateSheetDialogId: uniqueDialogId('rate-sheet-detail-dialog'),
    tractDetailDialogId: uniqueDialogId('tract-group-tract-detail-dialog'),
    focusedTractId: null,
    editingTractId: null
  }),

  computed: {
    ...mapGetters('user', ['businessEntities']),
    ...mapGetters('tract-type', ['allTractTypes']),
    generalInformationRows () {
      if (!this.tractGroup) return []
      const ns = this.$t('notSpecified')
      return [
        { key: 'code', value: this.tractGroup.code || ns },
        { key: 'type', value: this.tractGroup.tractTypeName },
        { key: 'category', value: this.categoryFromInt(this.tractGroup.tractTypeCategory) },
        { key: 'consultingForester', value: this.tractGroup.consultingForesterAccountName || ns },
        { key: 'forester', value: this.tractGroup.foresterUserName || ns },
        { key: 'team', value: this.tractGroup.teamName || ns },
        { key: 'businessEntity', value: this.tractGroup.businessEntityName || ns },
        { key: 'obligation', value: this.formatMoney(this.tractGroup.cost) },
        { key: 'obligationBalance', value: this.formatMoney(this.tractGroup.balance) },
        { key: 'harvestStartDate', value: this.tractGroup.harvestStartDate ? this.utcToLocalDate(this.tractGroup.harvestStartDate) : ns },
        { key: 'harvestEndDate', value: this.tractGroup.harvestEndDate ? this.utcToLocalDate(this.tractGroup.harvestEndDate) : ns }
      ]
    },

    landowners () {
      return this.tractGroup?.landowners || []
    },

    landownersPanelString () {
      if (this.landowners?.length <= 0) return ''
      return `${this.landowners.reduce((a, lo) => { return lo.ownership > a.ownership ? lo : a }).accountName}${this.landowners?.length > 1 ? ', ' + this.$t('nMore', { n: this.landowners.length - 1 }) : ''}`
    },

    landownerHeaders () {
      return [
        {
          text: this.$t('name'),
          value: 'accountName'
        },
        {
          text: this.$t('ownership'),
          value: 'ownership',
          align: 'right'
        }
      ]
    },

    tractHeaders () {
      return [
        {
          text: this.$t('status'),
          value: 'status',
          align: 'center',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('name'),
          value: 'name',
          align: 'left',
          sortable: true
        },
        {
          text: this.$t('code'),
          value: 'code',
          align: 'left',
          sortable: true,
          width: '150px'
        },
        {
          text: this.$t('actions'),
          value: 'actions',
          align: 'center',
          sortable: false,
          width: '80px'
        }
      ]
    },

    obligationRate () {
      return this.tractGroup?.namedRates.find(nr => nr.specialization === NamedRateSpecialization.Obligation.value)
    },

    purchasingAccountId () {
      return this.businessEntities.find(be => be.businessEntityId === this.tractGroup?.businessEntityId)?.accountId
    },

    optionalCardWidths () {
      return this.obligationRate && this.tractGroup?.landowners?.length > 0 ? 6 : 12
    }
  },

  watch: {
    currentTab (val) {
      this.$emit('resize', val === 2 ? '90vw' : '50vw')
    }
  },

  async created () {
    this.loading = true
    this.tractGroup = await this.fetchTractGroup(this.tractGroupId)
    if (this.businessEntities.length === 0) {
      await this.fetchAllBusinessEntities()
    }
    if (this.allTractTypes.length === 0) {
      await this.fetchTractTypes()
    }
    setTimeout(_ => { this.loading = false }, 150) // Smooth out loading
  },

  methods: {
    formatPercentage,
    formatMoney,
    utcToLocalDate,
    userAssignedClaim,
    ...mapActions('tract', ['fetchTractGroup']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('user', ['fetchAllBusinessEntities']),
    ...mapActions('tract-type', ['fetchTractTypes']),

    categoryFromInt (category) {
      return TractTypeCategory.fromInt(category)
    },

    async showRateSheetDialog () {
      if (this.obligationRate?.costCalculation?.rateSheet) {
        this.viewingRateSheetDetails = await this.fetchRateSheet(this.obligationRate?.costCalculation?.rateSheetId)
        this.openOrUpdateDialog({ id: this.rateSheetDialogId, width: '85vw' })
      }
    },

    closeRateSheetDialog () {
      this.viewingRateSheetDetails = undefined
      this.closeDialogsAtOrAbove(this.rateSheetDialogId)
    },

    onTractClick ({ data }) {
      this.focusedTractId = data.tractId
      this.openOrUpdateDialog({ id: this.tractDetailDialogId, width: '90vw' })
    },

    async closeTractDetailDialog () {
      this.focusedTractId = null
      this.editingTractId = null
      this.closeDialogsAtOrAbove(this.tractDetailDialogId)
      this.tractGroup = await this.fetchTractGroup(this.tractGroupId)
    },

    editTract (tract) {
      this.focusedTractId = null
      this.editingTractId = tract.tractId
    },

    openEditTract (tract) {
      this.focusedTractId = null
      this.editingTractId = tract.tractId
      this.openOrUpdateDialog({ id: this.tractDetailDialogId, width: '90vw' })
    }
  }
}
</script>
